/**
 * Provider-agnostic cookie consent abstraction.
 *
 * Guest Checkout depends only on this interface so the underlying consent
 * platform (currently Termly) can be swapped for another provider by adding a
 * new implementation — no consumer changes required.
 */

/**
 * Purpose categories a visitor can consent to, named independently of any
 * provider. `essential` is always granted: it covers storage the checkout
 * cannot function without.
 */
export interface ConsentCategories {
  readonly essential: boolean;
  readonly analytics: boolean;
  readonly performance: boolean;
  readonly advertising: boolean;
  readonly socialNetworking: boolean;
  readonly unclassified: boolean;
}

/** Every non-essential purpose refused. */
export const NO_CONSENT_CATEGORIES: ConsentCategories = {
  essential: true,
  analytics: false,
  performance: false,
  advertising: false,
  socialNetworking: false,
  unclassified: false,
};

/**
 * Notified whenever the visitor records or changes a decision.
 *
 * `categories` carries the same decision broken down by purpose, for consumers
 * (such as Google consent mode) that must distinguish analytics storage from
 * advertising storage.
 */
export type ConsentChangeCallback = (consented: boolean, categories: ConsentCategories) => void;

export interface CookieConsentProvider {
  /**
   * Whether the user has already made a consent decision (accepted or
   * declined), either on the current site or — where the decision is shared
   * across subdomains — elsewhere on `designmynight.com`.
   */
  hasResponded(): boolean;

  /**
   * Whether the user has actively consented to non-essential cookies.
   * Returns `false` when the user has declined or not yet responded.
   */
  hasConsented(): boolean;

  /**
   * The recorded decision broken down by purpose, or `null` when the visitor
   * has not decided anything yet.
   */
  getCategories(): ConsentCategories | null;

  /**
   * Render the consent banner. Implementations should be idempotent and a
   * no-op when a banner is already present.
   */
  showBanner(): void;

  /**
   * Whether this provider can present a preference centre in the current
   * context, i.e. whether a control offering one should be rendered at all.
   */
  canShowPreferences(): boolean;

  /**
   * Open the provider's preference centre so the visitor can revisit a
   * decision they have already made, including withdrawing consent.
   * Implementations should be a no-op when {@link canShowPreferences} is
   * `false`.
   */
  showPreferences(): void;

  /**
   * Subscribe to consent decision changes. The callback receives `true` when
   * the user consents and `false` when they decline.
   *
   * @returns an unsubscribe function that detaches the listener.
   */
  onConsentChange(callback: ConsentChangeCallback): () => void;
}
